import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { DocumentKind } from 'app/core/constants/document-center.constants';
import { OverlayLoaderDirective } from 'app/core/directives/overlay-loader.directive';
import { GlobalService } from 'app/core/global.service';
import { IdempotencyAction } from 'app/core/utils/idempotency.util';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { ScbApiService } from '../scb-api.service';
import { ScbSubmitMyBillingPaymentProofRequest } from '../scb.models';

export type ScbSubmitPaymentProofDialogData = {
  salesInvoiceId: string;
  invoiceNumber?: string | null;
  amount?: number | null;
  remainingAmount?: number | null;
};

type UploadResponse = { data?: { id?: string } };

@Component({
  selector: 'app-scb-submit-payment-proof-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatDialogActions,
    MatDialogContent,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    OverlayLoaderDirective,
  ],
  templateUrl: './scb-submit-payment-proof-dialog.component.html',
  styles: `::ng-deep .mat-mdc-dialog-container .mdc-dialog__surface { padding: 0 !important; }`,
})
export class ScbSubmitPaymentProofDialogComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly fb = inject(FormBuilder);
  private readonly api = inject(ScbApiService);
  private readonly globalService = inject(GlobalService);
  private readonly toast = inject(ToastrService);
  private readonly dialogRef = inject(MatDialogRef<ScbSubmitPaymentProofDialogComponent, boolean>);
  private readonly data = inject<ScbSubmitPaymentProofDialogData>(MAT_DIALOG_DATA);
  private readonly idempotency = new IdempotencyAction();

  readonly saving = signal(false);
  readonly uploading = signal(false);
  readonly fileName = signal<string | null>(null);

  readonly form = this.fb.group({
    amount: this.fb.control<number | null>(this.data.remainingAmount ?? this.data.amount ?? null, [
      Validators.min(0),
    ]),
    remarks: this.fb.control<string | null>(null),
    fileAttachmentId: this.fb.control<string | null>(null, Validators.required),
  });

  get titleHint(): string {
    return this.data.invoiceNumber?.trim() || 'Invoice';
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    this.uploading.set(true);
    this.globalService
      .uploadFile<UploadResponse>(file, DocumentKind.CustomerReceipt)
      .pipe(
        finalize(() => this.uploading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (res) => {
          const id = res?.data?.id;
          if (!id) {
            this.toast.error('File upload failed');
            return;
          }
          this.form.patchValue({ fileAttachmentId: id });
          this.fileName.set(file.name);
        },
        error: () => this.toast.error('File upload failed'),
      });
    input.value = '';
  }

  removeFile(): void {
    this.form.patchValue({ fileAttachmentId: null });
    this.fileName.set(null);
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      if (!this.form.get('fileAttachmentId')?.value) {
        this.toast.error('Please attach a payment proof file');
      }
      return;
    }
    const raw = this.form.getRawValue();
    const remarks = raw.remarks?.trim();
    const body: ScbSubmitMyBillingPaymentProofRequest = {
      salesInvoiceId: this.data.salesInvoiceId,
      fileAttachmentId: raw.fileAttachmentId,
      amount: raw.amount,
      remarks: remarks ? remarks : null,
    };

    this.saving.set(true);
    this.api
      .submitMyBillingPaymentProof(body, this.idempotency.getKey())
      .pipe(
        finalize(() => this.saving.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (resp) => {
          if (resp?.success !== false) {
            this.idempotency.reset();
            this.toast.success(resp?.message || 'Payment proof submitted');
            this.dialogRef.close(true);
          } else {
            this.toast.error(resp?.message || 'Unable to submit proof');
          }
        },
        error: (err: HttpErrorResponse) => {
          this.toast.error(err?.error?.message || 'Unable to submit proof');
        },
      });
  }
}
